import { Box, Button, Card, Flex, Grid, Icon, Text } from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteSkill, fetchSkillList } from '../../api/classification/classification'
import { BarChart012Icon, CheckCircleBrokenIcon, PlusIcon, Target04Icon, Trash04Icon } from '../../assets/icons'
import DiscardModal from '../modals/discardModal'
import AddSkillsModal from '../modals/addSkills'
import LoadingTable from './base/LoadingTable'
import MasterTable, { Column, RowData } from './base/MasterTable'
import Pagination from './base/Pagination'
import SearchInput from './base/SearchInput'
import FilterButton from './base/FilterButton'
import { useNavigate } from 'react-router-dom'
import { useRole } from '../../hooks/auth/useRole'
import { typeTag } from '../../themes/styles'

interface TableComponentProps {
  data: RowData[]
  setData: React.Dispatch<React.SetStateAction<RowData[]>>
}

const SkillTable: React.FC<TableComponentProps> = ({ data: _initialData, setData }) => {
  void _initialData
  const { t } = useTranslation()
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<{ [key: string]: string[] }>({})
  const itemsPerPage = 30
  const [isAddSkillsOpen, setIsAddSkillsOpen] = useState(false)

  const { isAdmin, isUserMode } = useRole()

  const [showDiscardModal, setShowDiscardModal] = useState(false)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const skills = useQuery({
    queryKey: ['skills', page, searchTerm, filters],
    queryFn: () =>
      fetchSkillList({
        search: searchTerm,
        page,
        pageSize: itemsPerPage,
        filters: filters.type ? { type: filters.type } : {},
      }),
  })

  const behaviourKeyMap: Record<string, string[]> = {
    competence: ['foundational', 'operational', 'tactical', 'strategic'],
    task: ['assisted', 'independent', 'complex', 'leadership'],
    skill: ['basic', 'intermediate', 'advanced', 'specialist'],
  }

  const columns: Column[] = [
    { label: '', accessor: 'select', type: 'select', width: '5%'  },
    {
      label: t('common.fields.name'),
      accessor: 'name',
      type: 'string',
      width: '25%',
      Cell: ({ row }) => <Text textStyle="l-xs">{row.name}</Text>,
    },
    {
      label: t('common.fields.type'),
      accessor: 'type',
      type: 'string',
      width: '25%',
      Cell: ({ row }) => {
        const colors = typeTag[row.type as keyof typeof typeTag] ?? typeTag.competence
        return (
          <Box
            display="flex"
            alignItems="center"
            textStyle="l-xs"
            color={colors.color}
            borderRadius="40px"
            borderWidth="1px"
            backgroundColor={colors.bg}
            borderColor={colors.borderColor}
            p="6px 12px 6px 10px"
            gap="4px"
            w={row.type === 'skill' ? '62px' : row.type === 'task' ? '70px' : '117px'}
            h="28px"
          >
            <Icon width="12px" height="12px" fill="none" color={colors.color}>
              {row.type === 'skill' ? (
                <BarChart012Icon />
              ) : row.type === 'task' ? (
                <Target04Icon />
              ) : (
                <CheckCircleBrokenIcon />
              )}
            </Icon>
            <Text textStyle="h-xxs">
              {row.type === 'skill'
                ? t('content.types.skills')
                : row.type === 'task'
                  ? t('content.types.task')
                  : t('content.types.competence')}
            </Text>
          </Box>
        )
      },
    },
    {
      label: t('classifications.skills.table.columns.behaviouralExamples'),
      accessor: 'behavioral_examples',
      type: 'string',
      Cell: ({ row }) => {
        const allowedKeys = behaviourKeyMap[row.type as keyof typeof behaviourKeyMap]
        const examples = allowedKeys
          ? allowedKeys.flatMap((key) => row.behavioural_examples?.[key] || [])
          : Object.values(row.behavioural_examples || {}).flat()
        const joinedExamples = examples.join(' ')
        return (
          <Text textStyle="l-xs" isTruncated maxW="500px">
            {joinedExamples ? joinedExamples : t('common.labels.na')}
          </Text>
        )
      },
    },
    { label: '', accessor: 'actions', type: 'actions' },
  ]

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const updateFilter = (key: string, values: string[]) => {
    setFilters(prev => {
      const next = { ...prev }
      if (values.length === 0) delete next[key]
      else next[key] = values
      return next
    })
    setPage(1)
  }

  const deleteSkillMutation = useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['skills'] })
    },
  })

  const handleDelete = () => {
    setShowDiscardModal(true)
  }

  const handleConfirmDelete = () => {
    selectedRows.forEach((skillId) => {
      deleteSkillMutation.mutate(skillId, {
        onSuccess: () => {
          setData((prevData) => prevData.filter((row) => row.id !== skillId))
          queryClient.invalidateQueries({ queryKey: ['skills'] })
        },
      })
    })
    setSelectedRows([])
    setShowDiscardModal(false)
  }

  const handleOpenNewTab = (row: RowData) => {
    const path = `/user/classification/skills/${row.id}`
    window.open(path, '_blank')
  }

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedRows(skills.data?.data?.map((row: RowData) => row.id) || [])
    } else {
      setSelectedRows([])
    }
  }

  const handleSelectRow = (id: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedRows((prevSelected) => [...prevSelected, id])
    } else {
      setSelectedRows((prevSelected) => prevSelected.filter((rowId) => rowId !== id))
    }
  }

  const allSelected =
    skills.data?.data &&
    skills.data.data.length > 0 &&
    skills.data.data.every((row: RowData) => selectedRows.includes(row.id))
  const isIndeterminate =
    skills.data?.data && skills.data.data.some((row: RowData) => selectedRows.includes(row.id)) && !allSelected

  const handleRowClick = (row: RowData) => {
    navigate(`/user/classification/skills/${row.id}`)
  }

  return (
    <Card width="100%" p="0">
      <Grid>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" height="64px" flexWrap="wrap" gap={4} px="20px">
            <SearchInput onSearch={handleSearch} />
            <Flex gap="8px">
              {selectedRows.length > 0 ? (
                <Button 
                  onClick={handleDelete} 
                  variant="error"
                  size="sm"
                  h="32px"
                  px="14px"
                  fontSize="13px"
                  fontWeight="600"
                  borderRadius="8px"
                >
                  <Icon fill="none" color="error.100" mr={2}><Trash04Icon /></Icon>
                  {t('workforce.people.deleteCount', { count: selectedRows.length })}
                </Button>
              ) : (
                <>
                  <FilterButton
                    label={t('common.fields.types')}
                    options={[
                      { id: 'skill', name: t('content.types.skills') },
                      { id: 'competence', name: t('content.types.competence') },
                      { id: 'task', name: t('content.types.task') },
                    ]}
                    onApply={(v) => updateFilter('type', v)}
                  />
                  {isAdmin && !isUserMode && (
                    <Button
                      variant="mono"
                      size="sm"
                      h="32px"
                      px="14px"
                      gap="8px"
                      onClick={() => setIsAddSkillsOpen(true)}
                    >
                      <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="white"><PlusIcon /></Icon>
                      {t('common.actions.create')}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
          {skills.isLoading ? (
            <LoadingTable columns={columns} />
          ) : (
            <Box minHeight="58vh">
              <MasterTable
                columns={columns}
                data={skills.data?.data || []}
                onRowClick={handleRowClick}
                onOpenNewTab={handleOpenNewTab}
                selectedRows={selectedRows}
                onSelectAll={handleSelectAll}
                onSelectRow={handleSelectRow}
                allSelected={allSelected}
                isIndeterminate={isIndeterminate}
                tableType="skills"
              />
            </Box>
          )}
          <Pagination
            currentPage={page}
            itemsPerPage={itemsPerPage}
            maxPage={skills.data?.max_page || 0}
            totalItems={skills.data?.count || 0}
            handlePreviousPage={() => setPage(page - 1)}
            handleNextPage={() => setPage(page + 1)}
            handlePageClick={(page: number) => setPage(page)}
          />
        </Box>
      </Grid>
      <DiscardModal
        show={showDiscardModal}
        handleClose={() => setShowDiscardModal(false)}
        handleDelete={handleConfirmDelete}
        action="delete"
        selectedRowsCount={selectedRows.length}
      />
      <AddSkillsModal isOpen={isAddSkillsOpen} onClose={() => setIsAddSkillsOpen(false)} />
    </Card>
  )
}

export default SkillTable
